import React from "react";
import { useQuery } from "@tanstack/react-query";
import { BookOpen, Layers, DoorOpen, FileText } from "lucide-react";
import api from "../../lib/api";
import { exportData } from "../../lib/exporter";
import { Button } from "../../components/ui/button";
import DaySelector from "./DaySelector";
import { groupBy } from "./helpers";

export default function PulizieRegistro({ structureId, data, setData, strutturaNome, companyLogo }) {
  const { data: checklist = [], isLoading: clLoading } = useQuery({
    enabled: !!structureId,
    queryKey: ["pul-checklist", structureId, data],
    queryFn: async () => (await api.get("/pulizie/checklist", { params: { structure_id: structureId, data } })).data,
  });
  const doneCount = checklist.filter((c) => c.fatto).length;
  const regGroups = groupBy(checklist);

  const exportRegistroPDF = () => {
    const rows = [...checklist]
      .sort((a, b) => (a.area_nome || "").localeCompare(b.area_nome || "", "it") || (a.stanza_nome || "").localeCompare(b.stanza_nome || "", "it"))
      .map((it) => [it.area_nome || "—", it.stanza_nome || "—", `${it.descrizione}${it.elemento_nome ? ` · ${it.elemento_nome}` : ""}`, it.fatto ? "Completata" : "Non fatta", it.note || "", it.foto ? "Sì" : "", it.operatore_nome || "—", (it.ts || "").slice(11, 16)]);
    exportData({ format: "pdf", filename: `registro_pulizie_${data}`, title: "Registro Pulizie", subtitle: `${strutturaNome} · ${data}`, columns: ["Area", "Stanza", "Attività", "Stato", "Verifica task", "Foto", "Operatore", "Ora"], rows, color: "#0284c7", logo: companyLogo });
  };

  return (
    <div data-testid="pul-registro">
      <DaySelector data={data} setData={setData} extra={
        <>
          <span className="text-sm font-semibold rounded-lg px-3 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200">{doneCount}/{checklist.length} completate</span>
          {checklist.some((c) => c.note) && <span className="text-sm font-semibold rounded-lg px-3 py-1.5 bg-sky-50 text-sky-700 border border-sky-200">{checklist.filter((c) => c.note).length} verifiche task</span>}
          {(checklist.length - doneCount) > 0 && <span className="text-sm font-semibold rounded-lg px-3 py-1.5 bg-red-50 text-red-700 border border-red-200">{checklist.length - doneCount} arretrate</span>}
          <Button variant="outline" size="sm" onClick={exportRegistroPDF} disabled={checklist.length === 0} data-testid="pul-reg-pdf" className="ml-auto font-semibold"><FileText size={15} className="mr-1" /> Scarica PDF</Button>
        </>
      } />

      {clLoading ? <p className="text-muted-foreground">Caricamento…</p> : checklist.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground"><BookOpen className="mx-auto mb-3 opacity-40" size={40} /><p>Nessuna pulizia programmata per questa data.</p></div>
      ) : (
        <div className="space-y-5">
          {Object.entries(regGroups).map(([area, stanze]) => (
            <div key={area} className="bg-white rounded-xl border overflow-hidden">
              <div className="px-4 py-2.5 bg-slate-50 border-b font-bold flex items-center gap-2"><Layers size={15} className="text-slate-500" /> {area}</div>
              <div className="divide-y">
                {Object.entries(stanze).map(([stanza, items]) => (
                  <div key={stanza} className="p-3">
                    {stanza !== "__area__" && <div className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1"><DoorOpen size={13} /> {stanza}</div>}
                    <div className="space-y-1">
                      {items.map((it) => (
                        <div key={it.attivita_id} className="flex items-center gap-3 text-sm py-1" data-testid={`pul-reg-item-${it.attivita_id}`}>
                          <span className={`w-2 h-2 rounded-full shrink-0 ${it.fatto ? "bg-emerald-500" : "bg-red-400"}`} />
                          <span className="flex-1 min-w-0 truncate">{it.descrizione}{it.elemento_nome ? ` · ${it.elemento_nome}` : ""}{it.note ? <span className="text-sky-700 font-medium"> — {it.note}</span> : ""}</span>
                          {it.foto && <a href={it.foto} target="_blank" rel="noreferrer" className="text-[11px] text-sky-600 underline shrink-0">foto</a>}
                          {it.fatto ? (
                            <span className="text-[11px] shrink-0 flex items-center gap-2">
                              <span className="font-bold text-emerald-700">Completata</span>
                              <span className="text-muted-foreground">{it.operatore_nome || "—"} · {(it.ts || "").slice(11, 16)}</span>
                            </span>
                          ) : <span className="text-[11px] font-semibold text-red-600 shrink-0">Non fatta</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
